import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  buildParticipantRows,
  getMeetingBySource,
  getParticipantDeviceLabel,
  MeetingSource,
  shiftTime
} from './meetingData';

type MonitorTab = 'audio' | 'video' | 'screen';
type EventResult = '成功' | '失败' | '—';
type TimeSortOrder = 'asc' | 'desc';
type EventFilterPanel = 'type' | 'result' | null;

interface MonitorDataset {
  receive: number[];
  send: number[];
}

interface EventRow {
  time: string;
  type: string;
  result: EventResult;
  detail: string;
}

interface FilterPopoverPosition {
  top: number;
  left: number;
}

const tabs: Array<{ key: MonitorTab; label: string }> = [
  { key: 'audio', label: '音频' },
  { key: 'video', label: '视频' },
  { key: 'screen', label: '屏幕共享' }
];

const createSeries = (seed: string, base: number, spread: number) => {
  const seedValue = seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return Array.from({ length: 16 }, (_, index) => {
    const wave = Math.sin((index + (seedValue % 7)) / 2.2) * spread;
    const pulse = index % 5 === 0 ? spread * 0.7 : 0;
    return Math.max(4, Math.min(96, base + wave + pulse));
  });
};

const buildPolylinePoints = (values: number[]) => {
  const width = 760;
  const height = 170;
  return values
    .map((value, index) => {
      const x = (width * index) / (values.length - 1);
      const y = height - (value / 100) * height;
      return `${x},${y}`;
    })
    .join(' ');
};

const createEvents = (startTime: string, name: string): EventRow[] => ([
  {
    time: shiftTime(startTime, 0),
    type: 'SDK初始化',
    result: '成功',
    detail: '浏览器 Chrome 138 / Windows 10'
  },
  {
    time: shiftTime(startTime, 1),
    type: '获取设备信息',
    result: '成功',
    detail: '摄像头 2 个'
  },
  {
    time: shiftTime(startTime, 2),
    type: '获取摄像头权限',
    result: '成功',
    detail: '—'
  },
  {
    time: shiftTime(startTime, 5),
    type: '创建摄像头视频轨道',
    result: '失败',
    detail: '无权限'
  },
  {
    time: shiftTime(startTime, 8),
    type: '加入会议',
    result: '成功',
    detail: `用户 ${name} 入会，耗时 11ms`
  },
  {
    time: shiftTime(startTime, 50),
    type: '开启麦克风',
    result: '成功',
    detail: '用户手动'
  },
  {
    time: shiftTime(startTime, 231),
    type: '视频卡顿',
    result: '—',
    detail: '持续 1043ms'
  }
]);

const parseTimeToSeconds = (value: string) => {
  const timePart = value.includes(' ') ? value.split(' ')[1] : value;
  const [hour, minute, second] = timePart.split(':').map(item => Number(item));
  return hour * 3600 + minute * 60 + second;
};

const isAllSelected = <T,>(selectedValues: T[], allOptions: T[]) =>
  allOptions.length > 0 && selectedValues.length === allOptions.length;

const renderChart = (title: string, dataset: MonitorDataset) => (
  <div className="monitor-chart-block">
    <div className="monitor-chart-title">{title}</div>
    <div className="monitor-chart-wrap">
      <svg viewBox="0 0 760 170" preserveAspectRatio="none">
        <polyline
          points={buildPolylinePoints(dataset.receive)}
          className="monitor-line monitor-line-main"
        />
        <polyline
          points={buildPolylinePoints(dataset.send)}
          className="monitor-line monitor-line-sub"
        />
      </svg>
    </div>
  </div>
);

const ParticipantDetail: React.FC = () => {
  const { meetingId, participantId } = useParams<{ meetingId: string; participantId: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<MonitorTab>('audio');
  const [timeSortOrder, setTimeSortOrder] = useState<TimeSortOrder>('asc');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedResults, setSelectedResults] = useState<EventResult[]>([]);
  const [draftTypes, setDraftTypes] = useState<string[]>([]);
  const [draftResults, setDraftResults] = useState<EventResult[]>([]);
  const [activeFilterPanel, setActiveFilterPanel] = useState<EventFilterPanel>(null);
  const [filterPopoverPosition, setFilterPopoverPosition] = useState<FilterPopoverPosition | null>(null);
  const typeFilterButtonRef = useRef<HTMLButtonElement | null>(null);
  const resultFilterButtonRef = useRef<HTMLButtonElement | null>(null);
  const source: MeetingSource = location.pathname.startsWith('/historical/') ? 'historical' : 'real-time';
  const sourceLabel = source === 'historical' ? '历史会议' : '实时会议';

  const meeting = getMeetingBySource(source, meetingId);
  const participants = useMemo(
    () => (meeting ? buildParticipantRows(meeting, source) : []),
    [meeting, source]
  );
  const participant = useMemo(
    () => participants.find(item => item.id === participantId),
    [participants, participantId]
  );
  const joinTime = participant?.joinTime ?? '';
  const endTime = participant
    ? (participant.leaveTime === '—' ? shiftTime(participant.joinTime, 36) : participant.leaveTime)
    : '';

  const monitorDataset = useMemo(() => {
    const seed = `${participant?.id ?? 'unknown'}-${activeTab}`;
    return {
      receive: createSeries(seed, 52, activeTab === 'audio' ? 30 : 24),
      send: createSeries(`${seed}-send`, 35, activeTab === 'audio' ? 20 : 16)
    };
  }, [participant, activeTab]);

  const eventRows = useMemo(
    () => (participant ? createEvents(participant.joinTime, participant.name) : []),
    [participant]
  );
  const eventTypeOptions = useMemo(
    () => Array.from(new Set(eventRows.map(item => item.type))),
    [eventRows]
  );
  const eventResultOptions = useMemo(
    () => Array.from(new Set(eventRows.map(item => item.result))),
    [eventRows]
  );
  const filteredEventRows = useMemo(() => {
    const rows = eventRows.filter(item => {
      const matchedType =
        selectedTypes.length === 0 || isAllSelected(selectedTypes, eventTypeOptions) || selectedTypes.includes(item.type);
      const matchedResult =
        selectedResults.length === 0 || isAllSelected(selectedResults, eventResultOptions) || selectedResults.includes(item.result);
      return matchedType && matchedResult;
    });

    return rows.sort((left, right) => {
      const diff = parseTimeToSeconds(left.time) - parseTimeToSeconds(right.time);
      return timeSortOrder === 'asc' ? diff : -diff;
    });
  }, [eventResultOptions, eventRows, eventTypeOptions, selectedResults, selectedTypes, timeSortOrder]);

  const isTypeFilterApplied = selectedTypes.length > 0 && !isAllSelected(selectedTypes, eventTypeOptions);
  const isResultFilterApplied = selectedResults.length > 0 && !isAllSelected(selectedResults, eventResultOptions);
  const hasDraftSelection = activeFilterPanel === 'type' ? draftTypes.length > 0 : draftResults.length > 0;

  const handleToggleTypeFilter = (value: string) => {
    setDraftTypes(prev =>
      prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
    );
  };

  const handleToggleResultFilter = (value: EventResult) => {
    setDraftResults(prev =>
      prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
    );
  };

  const syncFilterPopoverPosition = (panel: EventFilterPanel) => {
    const target =
      panel === 'type' ? typeFilterButtonRef.current : panel === 'result' ? resultFilterButtonRef.current : null;

    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const popoverWidth = 220;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(viewportPadding, rect.right - popoverWidth),
      window.innerWidth - popoverWidth - viewportPadding
    );

    setFilterPopoverPosition({
      top: rect.bottom + 8,
      left
    });
  };

  const openFilterPanel = (panel: Exclude<EventFilterPanel, null>) => {
    if (panel === 'type') {
      setDraftTypes(selectedTypes.length === 0 ? eventTypeOptions : selectedTypes);
    } else {
      setDraftResults(selectedResults.length === 0 ? eventResultOptions : selectedResults);
    }
    setActiveFilterPanel(panel);
    syncFilterPopoverPosition(panel);
  };

  const closeFilterPanel = () => {
    setActiveFilterPanel(null);
    setFilterPopoverPosition(null);
  };

  const handleApplyFilter = () => {
    if (activeFilterPanel === 'type') {
      setSelectedTypes(draftTypes);
    }

    if (activeFilterPanel === 'result') {
      setSelectedResults(draftResults);
    }

    closeFilterPanel();
  };

  const handleCancelFilter = () => {
    if (activeFilterPanel === 'type') {
      setDraftTypes(selectedTypes.length === 0 ? eventTypeOptions : selectedTypes);
    }

    if (activeFilterPanel === 'result') {
      setDraftResults(selectedResults.length === 0 ? eventResultOptions : selectedResults);
    }

    closeFilterPanel();
  };

  useEffect(() => {
    if (!activeFilterPanel) {
      return undefined;
    }

    const handleViewportChange = () => {
      syncFilterPopoverPosition(activeFilterPanel);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelFilter();
      }
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeFilterPanel, draftResults, draftTypes, selectedResults, selectedTypes]);

  const filterPopover = activeFilterPanel && filterPopoverPosition
    ? createPortal(
        <div
          className="event-filter-popover"
          style={{
            top: filterPopoverPosition.top,
            left: filterPopoverPosition.left
          }}
        >
          {activeFilterPanel === 'type' ? (
            <>
              <label className="event-filter-option">
                <input
                  type="checkbox"
                  checked={isAllSelected(draftTypes, eventTypeOptions)}
                  onChange={() => setDraftTypes(
                    isAllSelected(draftTypes, eventTypeOptions) ? [] : eventTypeOptions
                  )}
                />
                <span>全部</span>
              </label>
              {eventTypeOptions.map(option => (
                <label key={option} className="event-filter-option">
                  <input
                    type="checkbox"
                    checked={draftTypes.includes(option)}
                    onChange={() => handleToggleTypeFilter(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </>
          ) : (
            <>
              <label className="event-filter-option">
                <input
                  type="checkbox"
                  checked={isAllSelected(draftResults, eventResultOptions)}
                  onChange={() => setDraftResults(
                    isAllSelected(draftResults, eventResultOptions) ? [] : eventResultOptions
                  )}
                />
                <span>全部</span>
              </label>
              {eventResultOptions.map(option => (
                <label key={option} className="event-filter-option">
                  <input
                    type="checkbox"
                    checked={draftResults.includes(option)}
                    onChange={() => handleToggleResultFilter(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </>
          )}
          <div className="event-filter-actions">
            <button type="button" className="btn btn-secondary btn-small" onClick={handleCancelFilter}>
              取消
            </button>
            <button
              type="button"
              className="btn btn-primary btn-small"
              onClick={handleApplyFilter}
              disabled={!hasDraftSelection}
            >
              确认
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  const monitorTitle = tabs.find(tab => tab.key === activeTab)?.label ?? '音频';

  if (!meeting || !participant) {
    return (
      <div className="page-block">
        <div className="breadcrumb">
          <span>会议</span>
          <span className="breadcrumb-sep" aria-hidden="true">›</span>
          <Link to={`/${source}`} className="breadcrumb-link">{sourceLabel}</Link>
          <span className="breadcrumb-sep" aria-hidden="true">›</span>
          <span>{meetingId}</span>
        </div>
        <div className="page-card">
          <div className="table-empty">未找到该参会人详情</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-block">
      <div className="breadcrumb">
        <span>会议</span>
        <span className="breadcrumb-sep" aria-hidden="true">›</span>
        <Link to={`/${source}`} className="breadcrumb-link">{sourceLabel}</Link>
        <span className="breadcrumb-sep" aria-hidden="true">›</span>
        <Link to={`/${source}/${meetingId}`} className="breadcrumb-link">{meetingId}</Link>
        <span className="breadcrumb-sep" aria-hidden="true">›</span>
        <span>{participant.name}</span>
      </div>
      <div className="page-card">
        <section className="detail-section">
          <h3 className="detail-section-title">参会者信息</h3>
          <table className="detail-info-table">
            <tbody>
              <tr>
                <th>姓名</th>
                <td>{participant.name}</td>
                <th>账号</th>
                <td>{participant.account}</td>
              </tr>
              <tr>
                <th>邮箱</th>
                <td>{participant.email}</td>
                <th>设备</th>
                <td>{getParticipantDeviceLabel(participant)}</td>
              </tr>
              <tr>
                <th>参会时间</th>
                <td>{participant.joinTime}</td>
                <th>离会时间</th>
                <td>{endTime}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="detail-section">
          <div className="participant-monitor-header">
              <div className="tab-list participant-tab-list">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="participant-monitor-tools">
              <div className="participant-time-chip">
                {joinTime}　-　{endTime}
              </div>
              <label className="participant-sender-filter">
                <span>发送端：</span>
                <select className="toolbar-select">
                  <option>全部</option>
                  <option>{participant.name}</option>
                </select>
              </label>
            </div>
          </div>

          <div className="participant-monitor-layout">
            <div className="participant-chart-panel">
              <div className="monitor-legend-row">
                <span className="legend-dot legend-blue" /> 正常
                <span className="legend-dot legend-green" /> 延迟
                <span className="legend-dot legend-orange" /> 抖动
                <span className="legend-dot legend-red" /> 平均丢失
              </div>
              {renderChart(`${monitorTitle}（接收）`, monitorDataset)}
              {renderChart(`${monitorTitle}（发送）`, {
                receive: monitorDataset.send,
                send: monitorDataset.receive.map(value => Math.max(5, value - 20))
              })}
            </div>

            <aside className="participant-event-panel">
              <div className="table-container participant-event-table-container">
                <table className="compact-table participant-event-table">
                  <thead>
                    <tr>
                      <th>
                        <span className="event-header-control">
                          <span>时间</span>
                          <button
                            type="button"
                            className={`filter-icon-button event-sort-button ${timeSortOrder === 'desc' ? 'active' : ''}`}
                            aria-label="切换时间排序"
                            onClick={() => setTimeSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                          >
                            <span className={`event-sort-icon ${timeSortOrder}`} aria-hidden="true" />
                          </button>
                        </span>
                      </th>
                      <th>
                        <span className="event-header-control">
                          <span>事件类型</span>
                          <button
                            ref={typeFilterButtonRef}
                            type="button"
                            className={`filter-icon-button ${isTypeFilterApplied ? 'active' : ''}`}
                            aria-label="筛选事件类型"
                            onClick={() => (activeFilterPanel === 'type' ? handleCancelFilter() : openFilterPanel('type'))}
                          >
                            <span className="event-filter-icon" aria-hidden="true" />
                          </button>
                        </span>
                      </th>
                      <th>
                        <span className="event-header-control">
                          <span>结果</span>
                          <button
                            ref={resultFilterButtonRef}
                            type="button"
                            className={`filter-icon-button ${isResultFilterApplied ? 'active' : ''}`}
                            aria-label="筛选事件结果"
                            onClick={() => (activeFilterPanel === 'result' ? handleCancelFilter() : openFilterPanel('result'))}
                          >
                            <span className="event-filter-icon" aria-hidden="true" />
                          </button>
                        </span>
                      </th>
                      <th>事件详情</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEventRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="table-empty">暂无匹配事件</td>
                      </tr>
                    ) : (
                      filteredEventRows.map(item => (
                        <tr key={`${item.time}-${item.type}-${item.detail}`}>
                          <td>{item.time.split(' ')[1]}</td>
                          <td>{item.type}</td>
                          <td>{item.result}</td>
                          <td>{item.detail}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </aside>
          </div>
        </section>
      </div>
      {filterPopover}
    </div>
  );
};

export default ParticipantDetail;
